import { Box, Skeleton, Stack } from '@mui/material';
import { Typography, Button } from '@sweep-io/sweep-design';
import { useSelector } from 'react-redux';
import { useRunOnce } from '../../common/useRunOnce';
import { useMemo, useState } from 'react';
import { CARD_HEIGHT, PAGE_SIDE_PADDING } from '../../../constants';
import useSortWidget from '../../common/useSortWidget';
import { DefaultCardsLayout } from '../../common/CardsGridLayout';
import RestrictedTooltip from '../../common/permissions/RestrictedTooltip';
import usePermission from '../../common/permissions/usePermission';
import { useFunnelMaps } from './useFunnelMaps';
import { FunnelMapCard } from './card/FunnelMapCard';
import times from 'lodash/times';
import { SearchInput } from '../../common/SearchInput';
import sortFunnelMapsListByDate from './helpers/sortFunnelMapsListByDate';
import { selectPinnedFunnelMap } from '../../../reducers/funnelMapsReducer';
import { useNonPinnedFunnelMaps } from './useNonPinnedFunnelMaps';
import { useRequestChangesApiFacade } from '../../../apis/facades/useRequestChangesApiFacade';
import { useRunOnceWhenTruthy } from '../../common/useRunOnceWhenTruthy';
import { Permission } from '@server/permissions';
import EmptyStateWithButton from '../../common/empty-state/EmptyStateWithButton';
import FunnelsEmptyImage from '../../common/empty-state/svgs/funnelsEmpty.svg?react';
import { escapeRegexp } from '../../../lib/escapeRegex';
import { selectHasConnectedCrmOrgs } from '../environments/environmentsReducer';
import useSendBiEvent from '../../../hooks/useSendBiEvent';
import { UI_EVENTS } from '../../../services/events';
import { ConnectToSfPage } from '../../dashboard/paywall/sales-activated-trial/ConnectToSfPage';
import { AccountType } from '@server/account-type';
import { selectUserInfoData } from '../../../reducers/userInfoReducer';

const LAST_MODIFIED = 'Last modified';
const LAST_CREATED = 'Last created';

const options = [LAST_MODIFIED, LAST_CREATED];

const showOnlyFirstRowStyle = {
  maxHeight: CARD_HEIGHT,
  overflow: 'hidden',
};

const calculateVariant = (idx: number) => {
  const MAX_PREVIEWS_COUNT = (window as any).MAX_FUNNEL_MAPS_PREVIEWS_COUNT ?? -1;
  if (MAX_PREVIEWS_COUNT === -1) {
    return 'preview';
  }
  if (idx >= MAX_PREVIEWS_COUNT) {
    return 'placeholder';
  }
  return 'preview';
};

export const ADD_NOT_ALLOWED_TITLE = 'To add new funnel view, please contact your admin.';
const createNewFunnelMapDataTestId = 'create-new-funnel-map-button';

const FunnelMapsPage = () => {
  const [isLoadingFunnels, setIsLoadingFunnels] = useState(true);
  const { funnelMapsList, isLoading: isReducerLoading } = useNonPinnedFunnelMaps();

  const { fetchFunnelMaps, createNewFunnelMap } = useFunnelMaps();
  const { getRequestChanges } = useRequestChangesApiFacade();
  const userInfo = useSelector(selectUserInfoData);

  const isUserFree = userInfo?.account?.type === AccountType.Free;
  const hasAnyOrgs = useSelector(selectHasConnectedCrmOrgs);

  const [searchValue, setSearchValue] = useState('');

  const pinnedFunnelMap = useSelector(selectPinnedFunnelMap);

  const permissionStr: Permission[] = ['create:funnel-maps'];
  const [isAllowedButton] = usePermission(permissionStr);

  // w need to make sure it's loaded locally before we render, but also
  // we need to make sure it is loaded at the reducer level.
  // We use the local isLoadingFunnels state to solve a multiple re-render
  // issue that was causing performance issues.
  const combinedIsLoading = isLoadingFunnels || isReducerLoading;

  useRunOnce(async () => {
    await fetchFunnelMaps({ includeFunnelsData: true });
    setIsLoadingFunnels(false);
  });

  useRunOnceWhenTruthy(
    () => {
      const allFunnelMaps = [...funnelMapsList];
      if (pinnedFunnelMap) {
        allFunnelMaps.push(pinnedFunnelMap);
      }

      const funnelIdsSet = allFunnelMaps.reduce((acc, curr) => {
        Object.keys(curr.funnels).forEach((id) => acc.add(id));
        return acc;
      }, new Set<string>());

      getRequestChanges(Array.from(funnelIdsSet));
    },
    funnelMapsList.length > 0 || !!pinnedFunnelMap,
  );

  const { SortWidget, sortBy } = useSortWidget({
    defaultValue: LAST_MODIFIED,
    sortOptions: options,
  });

  const filteredAndSortedFunnelMapsList = useMemo(() => {
    const filteredList = funnelMapsList.filter((funnelMap) => {
      const regexp = new RegExp(escapeRegexp(searchValue), 'gi');
      return funnelMap.name.match(regexp);
    });

    return sortFunnelMapsListByDate({
      list: filteredList,
      sortKey: sortBy === LAST_CREATED ? 'createdAt' : 'updatedAt',
    });
  }, [searchValue, funnelMapsList, sortBy]);

  const sendBiEvent = useSendBiEvent();
  const onAddFunnelClick = () => {
    sendBiEvent({ name: UI_EVENTS.funnelsOpen, props: { mode: 'new' } });
    createNewFunnelMap();
  };

  const isEmpty = !combinedIsLoading && funnelMapsList.length === 0 && !pinnedFunnelMap;

  const renderHeader = () => {
    return <Typography variant="h2">Visual Workspace</Typography>;
  };

  const forceDisallowTitle = hasAnyOrgs
    ? undefined
    : 'You need to connect at least one environment to create a new funnel view';

  if (isUserFree && !hasAnyOrgs) {
    return <ConnectToSfPage />;
  }

  return (
    <>
      <Stack px={PAGE_SIDE_PADDING + 'px'} py={4} pt={4} data-testid="funnel-maps-page">
        {!isEmpty && (
          <Stack gap={3}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              {renderHeader()}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                {SortWidget}
                <Box>
                  <SearchInput
                    withFixedMagnifyingGlassIcon
                    placeholder="Search"
                    TextFieldProps={{
                      value: searchValue,
                      onChange: (event) => setSearchValue(event.target.value),
                    }}
                    onClearButtonClick={() => setSearchValue('')}
                    size={'small'}
                  />
                </Box>

                <RestrictedTooltip
                  to={permissionStr}
                  notAllowedTitle={ADD_NOT_ALLOWED_TITLE}
                  forceDisallowTitle={forceDisallowTitle}
                >
                  <Button
                    size={'small'}
                    dataTestId={createNewFunnelMapDataTestId}
                    disabled={!isAllowedButton || !hasAnyOrgs}
                    startIconName={'Plus'}
                    onClick={onAddFunnelClick}
                  >
                    New
                  </Button>
                </RestrictedTooltip>
              </Box>
            </Box>

            {pinnedFunnelMap && (
              <FunnelMapCard funnelMap={pinnedFunnelMap} isPinned={true} variant="preview" />
            )}

            <Box sx={combinedIsLoading ? showOnlyFirstRowStyle : {}}>
              <DefaultCardsLayout>
                {combinedIsLoading ? (
                  <SkeletonFunnelCards />
                ) : (
                  <>
                    {filteredAndSortedFunnelMapsList.map((funnelMap: FunnelMap, idx: number) => (
                      <FunnelMapCard
                        key={funnelMap.id}
                        funnelMap={funnelMap}
                        isPinned={false}
                        variant={calculateVariant(idx)}
                      />
                    ))}
                  </>
                )}
              </DefaultCardsLayout>
            </Box>
          </Stack>
        )}

        {isEmpty && (
          <Stack>
            {renderHeader()}
            <Box display="flex" justifyContent="center" pt="6vh">
              <EmptyStateWithButton
                dataTestId={createNewFunnelMapDataTestId}
                image={<FunnelsEmptyImage />}
                line1="Optimize and create new business processes"
                line2="with funnel editor"
                onClick={onAddFunnelClick}
                permissionString={permissionStr}
                notAllowedTitle={ADD_NOT_ALLOWED_TITLE}
                forceDisallowTitle={forceDisallowTitle}
                buttonDisabled={!hasAnyOrgs}
              />
            </Box>
          </Stack>
        )}
      </Stack>
    </>
  );
};

const SkeletonFunnelCards = () => (
  //There is max 6 cards in a row
  <>
    {times(6, String).map((x, index) => (
      <Skeleton
        key={index}
        variant="rectangular"
        animation="wave"
        height={CARD_HEIGHT}
        sx={{ borderRadius: '6px' }}
      />
    ))}
  </>
);

export default FunnelMapsPage;
